import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  CategoryV2,
  InventoryItemV2,
  CartItemV2,
  CartTotalsV2,
  DailySummaryV2,
  fetchCategoriesV2API,
  fetchItemsV2API,
  searchItemsV2API,
  placeOrderV2API,
  fetchDailySummaryV2API,
  CreateOrderV2,
} from './billingV2API';

// =============================================
// ASYNC THUNKS
// =============================================

export const fetchCategoriesV2 = createAsyncThunk<
  CategoryV2[],
  void,
  { rejectValue: string }
>('billingV2/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    const result = await fetchCategoriesV2API();
    if (result.status && result.data) {
      return result.data;
    }
    return rejectWithValue(result.message || 'Failed to fetch categories');
  } catch (error) {
    return rejectWithValue('Failed to fetch categories');
  }
});

export const fetchItemsV2 = createAsyncThunk<
  { items: InventoryItemV2[]; meta: any },
  { categoryId?: string; search?: string; page?: number; limit?: number },
  { rejectValue: string }
>('billingV2/fetchItems', async (params, { rejectWithValue }) => {
  try {
    const result = await fetchItemsV2API(params);
    if (result.status) {
      return {
        items: Array.isArray(result.data) ? result.data : [],
        meta: (result as any).meta || { total: 0, page: 1, limit: 50, totalPages: 0 },
      };
    }
    return rejectWithValue(result.message || 'Failed to fetch items');
  } catch (error) {
    return rejectWithValue('Failed to fetch items');
  }
});

export const searchItemsV2 = createAsyncThunk<
  InventoryItemV2[],
  { query: string; categoryId?: string },
  { rejectValue: string }
>('billingV2/searchItems', async ({ query, categoryId }, { rejectWithValue }) => {
  try {
    const result = await searchItemsV2API(query, categoryId);
    if (result.status) {
      return Array.isArray(result.data) ? result.data : [];
    }
    return rejectWithValue(result.message || 'Search failed');
  } catch (error) {
    return rejectWithValue('Search failed');
  }
});

export const placeOrderV2 = createAsyncThunk<
  { bill: any; items: any[] },
  CreateOrderV2,
  { rejectValue: string }
>('billingV2/placeOrder', async (orderData, { rejectWithValue }) => {
  try {
    const result = await placeOrderV2API(orderData);
    if (result.status) {
      return result.data!;
    }
    return rejectWithValue(result.message || 'Failed to place order');
  } catch (error) {
    return rejectWithValue('Failed to place order');
  }
});

export const fetchDailySummaryV2 = createAsyncThunk<
  DailySummaryV2,
  string | undefined,
  { rejectValue: string }
>('billingV2/fetchDailySummary', async (date, { rejectWithValue }) => {
  try {
    const result = await fetchDailySummaryV2API(date);
    if (result.status) {
      return result.data!;
    }
    return rejectWithValue(result.message || 'Failed to fetch summary');
  } catch (error) {
    return rejectWithValue('Failed to fetch daily summary');
  }
});

// =============================================
// HELPERS
// =============================================

const calculateCartTotals = (items: CartItemV2[]): CartTotalsV2 => {
  let subtotal = 0;
  const totalDiscount = 0;
  let totalTax = 0;
  let totalQuantity = 0;

  items.forEach(item => {
    const itemSubtotal = item.quantity * item.unitPrice;
    const taxAmount = (itemSubtotal * item.tax) / 100;

    subtotal += itemSubtotal;
    totalTax += taxAmount;
    totalQuantity += item.quantity;
  });

  // Round
  subtotal = Math.round(subtotal * 100) / 100;
  totalTax = Math.round(totalTax * 100) / 100;
  const totalAmount = Math.round((subtotal - totalDiscount + totalTax) * 100) / 100;

  return { subtotal, totalDiscount, totalTax, totalAmount, totalQuantity };
};

// =============================================
// STATE
// =============================================

interface BillingV2State {
  // Categories
  categories: CategoryV2[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  selectedCategoryId: string | null; // null = "All"

  // Items
  items: InventoryItemV2[];
  itemsLoading: boolean;
  itemsError: string | null;
  itemsMeta: { total: number; page: number; limit: number; totalPages: number };

  // Search
  searchResults: InventoryItemV2[];
  searchLoading: boolean;
  searchQuery: string;

  // Cart
  cartItems: CartItemV2[];
  totals: CartTotalsV2;

  // Order
  orderLoading: boolean;
  orderError: string | null;
  lastOrder: { bill: any; items: any[] } | null;

  // Summary
  dailySummary: DailySummaryV2 | null;
  summaryLoading: boolean;
}

const initialState: BillingV2State = {
  categories: [],
  categoriesLoading: false,
  categoriesError: null,
  selectedCategoryId: null,

  items: [],
  itemsLoading: false,
  itemsError: null,
  itemsMeta: { total: 0, page: 1, limit: 50, totalPages: 0 },

  searchResults: [],
  searchLoading: false,
  searchQuery: '',

  cartItems: [],
  totals: { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 },

  orderLoading: false,
  orderError: null,
  lastOrder: null,

  dailySummary: null,
  summaryLoading: false,
};

// =============================================
// SLICE
// =============================================

const billingV2Slice = createSlice({
  name: 'billingV2',
  initialState,
  reducers: {
    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategoryId = action.payload;
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    addToCartV2: (state, action: PayloadAction<InventoryItemV2>) => {
      const item = action.payload;
      const existingIndex = state.cartItems.findIndex(ci => ci.inventoryItemId === item.uuid);

      if (existingIndex >= 0) {
        // Increment quantity
        state.cartItems[existingIndex].quantity += 1;
      } else {
        // Add new item — use item's own tax, track originalPrice
        state.cartItems.push({
          inventoryItemId: item.uuid,
          itemCode: item.item_code,
          itemName: item.item_name,
          quantity: 1,
          unitPrice: item.selling_price || 0,
          originalPrice: item.selling_price || 0,
          discount: 0,
          tax: item.tax || 0,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          image: item.product_image,
          description: item.description,
          availableStock: item.quantity_in_stock,
        });
      }

      state.totals = calculateCartTotals(state.cartItems);
    },

    removeFromCartV2: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      state.cartItems = state.cartItems.filter(ci => ci.inventoryItemId !== itemId);
      state.totals = calculateCartTotals(state.cartItems);
    },

    updateCartItemQuantityV2: (state, action: PayloadAction<{ itemId: string; quantity: number }>) => {
      const { itemId, quantity } = action.payload;
      const index = state.cartItems.findIndex(ci => ci.inventoryItemId === itemId);

      if (index >= 0) {
        if (quantity > 0) {
          state.cartItems[index].quantity = quantity;
        } else {
          state.cartItems.splice(index, 1);
        }
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    updateCartItemPriceV2: (state, action: PayloadAction<{ itemId: string; unitPrice: number }>) => {
      const { itemId, unitPrice } = action.payload;
      const index = state.cartItems.findIndex(ci => ci.inventoryItemId === itemId);
      if (index >= 0 && unitPrice >= 0) {
        state.cartItems[index].unitPrice = unitPrice;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    resetCartItemPriceV2: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const index = state.cartItems.findIndex(ci => ci.inventoryItemId === itemId);
      if (index >= 0 && state.cartItems[index].originalPrice !== undefined) {
        state.cartItems[index].unitPrice = state.cartItems[index].originalPrice!;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    updateCartItemDiscountV2: (state, action: PayloadAction<{ itemId: string; discount: number }>) => {
      const { itemId } = action.payload;
      const index = state.cartItems.findIndex(ci => ci.inventoryItemId === itemId);
      if (index >= 0) {
        state.cartItems[index].discount = 0;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    incrementQuantityV2: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const index = state.cartItems.findIndex(ci => ci.inventoryItemId === itemId);
      if (index >= 0) {
        state.cartItems[index].quantity += 1;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    decrementQuantityV2: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const index = state.cartItems.findIndex(ci => ci.inventoryItemId === itemId);
      if (index >= 0) {
        if (state.cartItems[index].quantity > 1) {
          state.cartItems[index].quantity -= 1;
        } else {
          state.cartItems.splice(index, 1);
        }
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    clearCartV2: (state) => {
      state.cartItems = [];
      state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
    },

    clearOrderState: (state) => {
      state.orderError = null;
      state.lastOrder = null;
    },

    resetAfterOrderV2: (state) => {
      state.cartItems = [];
      state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
      state.orderError = null;
      state.lastOrder = null;
    },
  },
  extraReducers: (builder) => {
    // Categories
    builder
      .addCase(fetchCategoriesV2.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchCategoriesV2.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategoriesV2.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload || 'Failed to fetch categories';
      });

    // Items
    builder
      .addCase(fetchItemsV2.pending, (state) => {
        state.itemsLoading = true;
        state.itemsError = null;
      })
      .addCase(fetchItemsV2.fulfilled, (state, action) => {
        state.itemsLoading = false;
        state.items = action.payload.items;
        state.itemsMeta = action.payload.meta;
      })
      .addCase(fetchItemsV2.rejected, (state, action) => {
        state.itemsLoading = false;
        state.itemsError = action.payload || 'Failed to fetch items';
      });

    // Search
    builder
      .addCase(searchItemsV2.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchItemsV2.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchItemsV2.rejected, (state) => {
        state.searchLoading = false;
        state.searchResults = [];
      });

    // Place Order
    builder
      .addCase(placeOrderV2.pending, (state) => {
        state.orderLoading = true;
        state.orderError = null;
      })
      .addCase(placeOrderV2.fulfilled, (state, action) => {
        state.orderLoading = false;
        state.lastOrder = action.payload;
        // Clear cart after successful order
        state.cartItems = [];
        state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
      })
      .addCase(placeOrderV2.rejected, (state, action) => {
        state.orderLoading = false;
        state.orderError = action.payload || 'Failed to place order';
      });

    // Daily Summary
    builder
      .addCase(fetchDailySummaryV2.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchDailySummaryV2.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.dailySummary = action.payload;
      })
      .addCase(fetchDailySummaryV2.rejected, (state) => {
        state.summaryLoading = false;
      });
  },
});

export const {
  setSelectedCategory,
  setSearchQuery,
  addToCartV2,
  removeFromCartV2,
  updateCartItemQuantityV2,
  updateCartItemPriceV2,
  resetCartItemPriceV2,
  updateCartItemDiscountV2,
  incrementQuantityV2,
  decrementQuantityV2,
  clearCartV2,
  clearOrderState,
  resetAfterOrderV2,
} = billingV2Slice.actions;

export default billingV2Slice.reducer;
